import React from 'react';
import type { EmailExclusionJob } from '../types';
import { useGetEmailExclusionJob } from 'query/acg/phase-1';
import { Alert, Icon, Spin, Tooltip } from 'antd';
import ExclusionJobUploadedFiles from './ExclusionJobUploadedFiles';
import ExclusionJobDetailsStatusBar from './ExclusionJobDetailsStatusBar';
import UserWithAvatar from 'components/ui/UserWithAvatar';
import moment from 'moment';
import FilenameWithDownloader from '../../../Phase2/FilenameWithDownloader';
import DownloadButtonRound from 'components/ui/DownloadButtonRound';
import _ from 'lodash';
import { CampaignFile } from '../types';
import PostExclusionCriteriaTable from './PostExclusionCriteriaTable';

type Props = {
  job: EmailExclusionJob,
};

export default function ViewEmailExclusionJobModalContent(props: Props) {
  const { data, isLoading } = useGetEmailExclusionJob(props.job.id);
  const { data: jobDetails = props.job, user } = data || {};
  return (
    <Spin spinning={isLoading}>
      <div className="w-full space-y-6">
        {jobDetails.status === 'failed' && (
          <Alert message={jobDetails.failureReason} showIcon type="error" />
        )}
        <div className="flex items-center w-full">
          <div className="space-y-2 flex-1 flex flex-col">
            <b>Uploaded By</b>
            <UserWithAvatar user={user} />
          </div>
          <div className="space-y-2 flex-1 flex flex-col">
            <b>Uploaded At</b>
            <div>{moment(jobDetails.createdAt).format('YYYY-MM-DD HH:mm')}</div>
          </div>
        </div>
        <ExclusionJobDetailsStatusBar job={jobDetails} />
        {jobDetails.status === 'processed' && (
          <div className="flex items-center w-full">
            <div className="space-y-2 flex-1 flex flex-col">
              <b>Output</b>
              <FilenameWithDownloader
                filename="output.zip"
                gcsPath={jobDetails.outputZipGcsUrl}
                phase="phase1"
              />
            </div>
            <div className="space-y-2 flex-1 flex flex-col">
              <b>Exclusion DB</b>
              <FilenameWithDownloader
                filename="exclusion_db.csv"
                gcsPath={jobDetails.exclusionDbGcsUrl}
                phase="phase1"
              />
            </div>
            <div className="space-y-2 flex-1 flex flex-col">
              <b>Abstract DB</b>
              <div className="flex items-center space-x-2">
                <DownloadButtonRound
                  onClick={() =>
                    handleDownloadAbstractDbAsCSV(jobDetails.campaignFiles)
                  }
                />
                <div>abstract_db.csv</div>
              </div>
            </div>
          </div>
        )}

        <div className="w-full space-y-2">
          <b>Uploaded Files</b>
          <ExclusionJobUploadedFiles job={jobDetails} />
        </div>

        <div className="w-full space-y-2">
          <b>Post Exclusion Criteria</b>
          <PostExclusionCriteriaTable job={jobDetails} />
        </div>
      </div>
    </Spin>
  );
}

const handleDownloadAbstractDbAsCSV = async (
  campaignsFiles: CampaignFile[],
) => {
  const data = _.orderBy(campaignsFiles, ['id'], ['asc']).map(e => ({
    folder: e.folder,
    filename: e.filename,
    beforeExclusion: e.beforeExclusionCount,
    afterExclusion: e.afterExclusionCount,
  }));
  const headers = Object.keys(data[0]);
  const csv = [
    headers.join(','),
    ...data.map(row => headers.map(header => row[header]).join(',')),
  ].join('\n');
  const encodedUri = encodeURI(`data:text/csv;charset=utf-8,${csv}`);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', 'abstract_db.csv');
  document.body.appendChild(link);
  link.click();
};
